import React, { useContext, useMemo, useRef } from 'react';

import { useHistory, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { Card, Dropdown, Menu } from 'antd';
import Icon from '@ant-design/icons';

import { getFormattedCurrencySymbol } from '../../../../../utils/currency';
import { actions } from '../../../../../slices/credits/creditPool/creditPool.slices';
import { IPerson, PillsGroup } from '../../../Pills/PillsGroup';
import { ReactComponent as ThreeDotsIcon } from '../../../../../assets/sightx-icons/svg/icon-menu.svg';
import { Alert } from '../../../modals/common/Alert';
import { useElementWidth } from '../../../hooks/useElementWith';
import { CreditsPoolsDashboardContext } from '../context';
import { CREDITS_PATH } from '../../../../common/navigationBar/accountsNavigationBar';
import { CreditsPoolView } from '../../../../../slices/credits/creditsPools/creditsPools.slice';
import { selectCurrentViewActiveVsArchived } from '../../../../../slices/credits/creditsPools/selectors';
import { selectors as authSelectors } from '../../../../../slices/auth';
import { selectors as hubspotAccountSelectors } from '../../../../../slices/accounts/hubspotAccounts';
import { Features, isFeatureEnabled } from '../../../../../featureToggles/features';
import { isNetquestClient } from '../../../../../utils/clients';
import { AutoEllipsisTooltip } from '../../../../common/autoEllipsisTooltip';

import styles from './index.module.scss';

const { setSelectedCreditPoolId } = actions;
const { isCreditPoolAdmin } = authSelectors;

interface ActionsMenuProps {
  onDelete: (event: React.MouseEvent) => void;
  onArchive: (event: React.MouseEvent) => void;
  showDelete?: boolean;
  archiveLabel?: string;
}

const stopPropagationHandle = (e: React.MouseEvent<HTMLElement>) => e.stopPropagation();

const ActionsMenu: React.FC<ActionsMenuProps> = ({ onArchive, onDelete, showDelete, archiveLabel }) => {
  const onDeleteWithStopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    stopPropagationHandle(e);
    onDelete(e);
  };
  const onArchiveWithStopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    stopPropagationHandle(e);
    onArchive(e);
  };
  const menu = (
    <Menu>
      {showDelete && <Menu.Item>
        <div onClick={onDeleteWithStopPropagation}>Delete</div>
      </Menu.Item>}
      <Menu.Item>
        <div onClick={onArchiveWithStopPropagation}>{archiveLabel || 'Archive'}</div>
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown trigger={['click']} overlay={menu} className={styles.dropdown} placement="bottomLeft">
      <Icon className={styles.icon} component={ThreeDotsIcon} onClick={stopPropagationHandle}/>
    </Dropdown>
  );
};

export interface ICreditsPoolOverview {
  id: string;
  credits: number;
  pending: number;
  finalized: number;
  name: string;
  users: ReadonlyArray<IPerson>;
  thresholdWarningMessage?: string;
  currencySymbol?: string;
}

const CreditsPoolOverview: React.FC<ICreditsPoolOverview> = React.memo(({ credits, name, users, id: key, pending, finalized, thresholdWarningMessage }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const url = match.url.split('/');
  const componentRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(componentRef); // TODO: Do a better calculation of width. Delegate to a parent higher in hierarchy
  const { onDeleteCreditsPoolClick, onArchiveCreditsPoolClick } = useContext(CreditsPoolsDashboardContext);
  const isCreditPoolEditor = useSelector(isCreditPoolAdmin);
  const isCreditPoolUserFacing = useSelector(authSelectors.isCreditPoolUserFacing);
  const user = useSelector(authSelectors.getLoggedInUser);
  const accountId = isCreditPoolUserFacing && user?.accountId ? user.accountId : url[2];
  const shouldUseHubspotForCurrency = isFeatureEnabled(Features.CreditPoolExtension) && isNetquestClient();
  const hubspotAccount = useSelector(hubspotAccountSelectors.getHubspotAccount);
  const currencySymbol = shouldUseHubspotForCurrency ? hubspotAccount?.currencySymbol : undefined;
  const isHubspotLoadingResult = useSelector(hubspotAccountSelectors.getLoading);
  const isHubspotLoading = shouldUseHubspotForCurrency && isHubspotLoadingResult;

  const onDeleteHandler = useMemo(() => {
    return (event: React.MouseEvent) => {
      onDeleteCreditsPoolClick(`${key}`, name);
      event.preventDefault();
    };
  }, [key, name, onDeleteCreditsPoolClick]);

  const onArchiveHandler = useMemo(() => {
    return (event: React.MouseEvent) => {
      onArchiveCreditsPoolClick(`${key}`, name);
      event.preventDefault();
    };
  }, [key, name, onArchiveCreditsPoolClick]);

  const redirectToCreditPoolDashboard = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    await dispatch(setSelectedCreditPoolId(key));
    if (isCreditPoolUserFacing) {
      history.push(`/settings/${CREDITS_PATH}/${accountId}/${key}`);
      return;
    }
    history.push(`/accounts/${accountId}/${CREDITS_PATH}/${key}`);
  };

  const showDelete = credits === 0 && (pending + finalized === 0);
  const currentView = useSelector(selectCurrentViewActiveVsArchived);
  const archiveLabel = currentView === CreditsPoolView.Active ? undefined : 'Remove from archived';
  const shouldShowThresholdWarning = isFeatureEnabled(Features.CreditPoolExtension) && isNetquestClient() && !!thresholdWarningMessage;

  return(
    <Card className={styles.card} onClick={redirectToCreditPoolDashboard}>
      <div className={styles.actionsContainer}>
        <div className={styles.actions}>
          { isCreditPoolEditor && <ActionsMenu showDelete={showDelete} onDelete={onDeleteHandler} onArchive={onArchiveHandler} archiveLabel={archiveLabel}/> }
        </div>
      </div>
      <div ref={componentRef} className={styles.contents}>
        <div className={styles.heading}>
          {!isHubspotLoading && <span className={cx({ [styles.negative]: credits < 0 })} >{getFormattedCurrencySymbol(credits, { currencySymbol })} credits</span>}
        </div>
        <div className={styles.name}><AutoEllipsisTooltip text={name} tooltipText={name} /></div>
        { shouldShowThresholdWarning && <Alert type='warning' message={thresholdWarningMessage} /> }
        <div className={styles.space}>
        </div>
        <div className={styles.userPills}>
          {
            (width) > 0 ? <PillsGroup users={users} intendedNumberOfLines={1} width={width} /> : null
          }
        </div>
      </div>
    </Card>
  );
});

export { CreditsPoolOverview };
